import React, { useEffect, useRef, useState } from "react";
import { useSpring, animated } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";
import "./Coupon.css";
import { couponMeshGradient } from "../utils/couponMeshGradient";
import { TICKET_VARIANT_CLASSIC, ticketPath } from "../utils/ticketPath";

/** Match `.coupon` width/height in Coupon.css */
const CARD_W = 104;
const CARD_H = 120;

const COUPON_SHAPE_PATH = ticketPath(CARD_W, CARD_H, TICKET_VARIANT_CLASSIC);

const spreadSpringConfig = { mass: 1, tension: 300, friction: 35 };

/** Monotonic z-index so the last-touched coupon stays above siblings in `.main`. */
let couponStackSeq = 10;
const nextCouponStackZ = () => {
  couponStackSeq += 1;
  return couponStackSeq;
};

const bounds = () => ({
  minX: 0,
  minY: 0,
  maxX: Math.max(0, window.innerWidth - CARD_W),
  maxY: Math.max(0, window.innerHeight - CARD_H),
});

/** Resting tilt range for layout and after each release (−20° … 20°). */
const randomRestRotate = () => Math.random() * 40 - 20;

/** Random position + tilt inside the viewport (same idea as initial mount, safe for small windows). */
const randomSpread = () => {
  const b = bounds();
  const rw = b.maxX - b.minX;
  const rh = b.maxY - b.minY;
  return {
    x: b.minX + (rw > 0 ? Math.random() * rw : 0),
    y: b.minY + (rh > 0 ? Math.random() * rh : 0),
    rotate: randomRestRotate(),
  };
};

const RESTITUTION = 0.78;

/** Integrate one step, resolve up to a few wall hits (fast flings). */
function integrateBounce(px, py, vx, vy, dt, b) {
  let x = px + vx * dt;
  let y = py + vy * dt;
  let vxx = vx;
  let vyy = vy;
  for (let i = 0; i < 4; i++) {
    let hit = false;
    if (x < b.minX) {
      x = b.minX;
      vxx = -vxx * RESTITUTION;
      hit = true;
    } else if (x > b.maxX) {
      x = b.maxX;
      vxx = -vxx * RESTITUTION;
      hit = true;
    }
    if (y < b.minY) {
      y = b.minY;
      vyy = -vyy * RESTITUTION;
      hit = true;
    } else if (y > b.maxY) {
      y = b.maxY;
      vyy = -vyy * RESTITUTION;
      hit = true;
    }
    if (!hit) break;
  }
  return { x, y, vx: vxx, vy: vyy };
}

/**
 * Signed fling velocity (px/ms). @use-gesture reports velocity as |Δ|/dt, so
 * components are never negative; we use Δ/timeDelta, or velocity×direction.
 */
function releaseVelocity(delta, timeDelta, velocity, direction) {
  const [d0, d1] = delta;
  const [v0, v1] = velocity;
  const [dir0, dir1] = direction;
  if (timeDelta > 0) {
    const td = Math.max(1, Math.min(48, timeDelta));
    return [d0 / td, d1 / td];
  }
  return [v0 * dir0, v1 * dir1];
}

/**
 * Draggable coupon card.
 * – Random starting position & rotation
 * – Smooth spring‑based movement
 * – Flick to toss; slides and bounces off screen edges
 * – Tap to trigger `onClick` without dragging
 */
const Coupon = ({
  emoji,
  name,
  active = true,
  description,
  onClick,
  relayoutKey = 0,
}) => {
  const [grabbing, setGrabbing] = useState(false);

  /** Resting rotation when the current grab began (wobble is added on top while held). */
  const heldRotateBaseRef = useRef(0);
  const grabT0Ref = useRef(0);
  const holdWobbleRafRef = useRef(0);

  const cancelHoldWobble = () => {
    if (holdWobbleRafRef.current) {
      cancelAnimationFrame(holdWobbleRafRef.current);
      holdWobbleRafRef.current = 0;
    }
  };

  // Remember where the card was released last (random start, viewport-safe)
  const last = useRef(
    (() => {
      const b = bounds();
      const rw = b.maxX - b.minX;
      const rh = b.maxY - b.minY;
      return {
        x: b.minX + (rw > 0 ? Math.random() * rw : 0),
        y: b.minY + (rh > 0 ? Math.random() * rh : 0),
      };
    })()
  );

  const surfaceZRef = useRef(1);

  const tossRafRef = useRef(0);

  const cancelToss = () => {
    if (tossRafRef.current) {
      cancelAnimationFrame(tossRafRef.current);
      tossRafRef.current = 0;
    }
  };

  useEffect(() => () => cancelToss(), []);

  const restingShadow = "0px 4px 8px rgba(0, 0, 0, 0.15)";
  const liftedShadow = "0 14px 28px rgba(0, 0, 0, 0.22), 0 6px 10px rgba(0, 0, 0, 0.12)";

  // Spring state
  const [{ x, y, rotate, scale, boxShadow, zIndex }, api] = useSpring(() => ({
    x: last.current.x,
    y: last.current.y,
    rotate: randomRestRotate(),
    scale: 1,
    boxShadow: restingShadow,
    zIndex: 1,
    config: spreadSpringConfig,
  }));

  /** Gentle left–right rock while the pointer is down (runs even if the finger stays still). */
  useEffect(() => {
    if (!grabbing) return undefined;

    const WOBBLE_HZ = 2.2;
    const WOBBLE_DEG = 6.5;

    const step = (now) => {
      const t = (now - grabT0Ref.current) / 1000;
      const wobble = Math.sin(t * Math.PI * 2 * WOBBLE_HZ) * WOBBLE_DEG;
      api.set({ rotate: heldRotateBaseRef.current + wobble });
      holdWobbleRafRef.current = requestAnimationFrame(step);
    };

    holdWobbleRafRef.current = requestAnimationFrame(step);
    return () => cancelHoldWobble();
  }, [grabbing, api]);

  useEffect(() => {
    if (relayoutKey === 0) return;
    cancelToss();
    const next = randomSpread();
    last.current = { x: next.x, y: next.y };
    api.start({
      x: next.x,
      y: next.y,
      rotate: next.rotate,
      scale: 1,
      boxShadow: restingShadow,
      zIndex: surfaceZRef.current,
      config: spreadSpringConfig,
    });
  }, [relayoutKey, api]);

  const startToss = (fromX, fromY, vx, vy) => {
    // use-gesture velocity is px/ms; tune with multiplier / thresholds
    const MIN_TOSS = 0.06;
    const STOP = 0.014;
    const MAX_SPEED = 3.2;
    const BOOST = 1.15;

    let vxr = vx * BOOST;
    let vyr = vy * BOOST;
    let sp = Math.hypot(vxr, vyr);
    if (sp < MIN_TOSS) return false;
    if (sp > MAX_SPEED) {
      const s = MAX_SPEED / sp;
      vxr *= s;
      vyr *= s;
    }

    cancelToss();
    cancelHoldWobble();

    last.current = { x: fromX, y: fromY };

    let px = fromX;
    let py = fromY;
    let velX = vxr;
    let velY = vyr;
    let prevTime = performance.now();

    api.set({
      scale: 1,
      boxShadow: restingShadow,
      rotate: randomRestRotate(),
    });

    const step = (now) => {
      const dt = Math.min(48, Math.max(0, now - prevTime));
      prevTime = now;
      if (dt === 0) {
        tossRafRef.current = requestAnimationFrame(step);
        return;
      }

      const b = bounds();
      const damp = Math.exp(-2.2 * (dt / 1000));
      velX *= damp;
      velY *= damp;

      const next = integrateBounce(px, py, velX, velY, dt, b);
      px = next.x;
      py = next.y;
      velX = next.vx;
      velY = next.vy;

      api.set({ x: px, y: py });
      last.current = { x: px, y: py };

      sp = Math.hypot(velX, velY);
      if (sp < STOP) {
        tossRafRef.current = 0;
        api.start({
          x: px,
          y: py,
          rotate: randomRestRotate(),
          scale: 1,
          boxShadow: restingShadow,
          zIndex: surfaceZRef.current,
          immediate: (key) =>
            key === "x" || key === "y" || key === "zIndex",
        });
        return;
      }

      tossRafRef.current = requestAnimationFrame(step);
    };

    tossRafRef.current = requestAnimationFrame(step);
    return true;
  };

  // Drag handler
  const bind = useDrag(
    ({
      down,
      first,
      movement: [mx, my],
      delta,
      timeDelta,
      velocity,
      direction,
      tap,
    }) => {
      // Short tap acts like a click
      if (tap) {
        onClick?.();
        setGrabbing(false);
        cancelToss();
        api.start({
          scale: 1,
          boxShadow: restingShadow,
          zIndex: surfaceZRef.current,
          immediate: true,
        });
        return;
      }

      if (down && first) {
        cancelToss();
        surfaceZRef.current = nextCouponStackZ();
        last.current = { x: x.get(), y: y.get() };
        heldRotateBaseRef.current = rotate.get();
        grabT0Ref.current = performance.now();
      }

      if (down) {
        if (first) setGrabbing(true);
        api.start({
          x: last.current.x + mx,
          y: last.current.y + my,
          scale: 1.08,
          boxShadow: liftedShadow,
          zIndex: surfaceZRef.current,
          immediate: (key) => {
            if (key === "x" || key === "y") return true;
            if (key === "zIndex") return true;
            return true;
          },
        });
        return;
      }

      setGrabbing(false);
      cancelHoldWobble();

      const rx = last.current.x + mx;
      const ry = last.current.y + my;

      const [vx, vy] = releaseVelocity(
        delta,
        timeDelta,
        velocity,
        direction
      );

      if (!startToss(rx, ry, vx, vy)) {
        last.current = { x: rx, y: ry };
        api.start({
          x: rx,
          y: ry,
          rotate: randomRestRotate(),
          scale: 1,
          boxShadow: restingShadow,
          zIndex: surfaceZRef.current,
          immediate: (key) => {
            if (key === "x" || key === "y") return true;
            if (key === "zIndex") return true;
            return false;
          },
        });
      }
    },
    { pointer: { touch: true } }
  );

  return (
    <animated.div
      {...bind()}
      className="coupon"
      style={{
        x,
        y,
        rotate,
        scale,
        clipPath: `path("${COUPON_SHAPE_PATH}")`,
        background: couponMeshGradient(name),
        boxShadow,
        zIndex,
        opacity: active ? 1 : 0.6,
        touchAction: "none",
        cursor: grabbing ? "grabbing" : "grab",
      }}
    >
      <div className="coupon-inner">
        <span className="coupon-emoji" aria-hidden>
          {emoji}
        </span>
        <span className="coupon-name">{name}</span>
      </div>
    </animated.div>
  );
};

export default Coupon;
